function hashEmail(email) {
  const norm = String(email || "")
    .toLowerCase()
    .trim();
  let hash = 0;
  for (let i = 0; i < norm.length; i++) {
    hash = norm.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
}

function normalizeAccountEmail(email) {
  return String(email || "")
    .toLowerCase()
    .trim();
}

/** Lista ordenada y única de emails de cuenta. */
export function normalizeConnectedEmails(connectedEmails = []) {
  return [
    ...new Set(
      (connectedEmails || [])
        .map(normalizeAccountEmail)
        .filter((e) => e.includes("@"))
    ),
  ].sort();
}

/**
 * Paleta fija bien distinguible (no HSL uniforme: evita teals/azules parecidos).
 * Orden pensado para contrastar cuentas consecutivas.
 */
const ACCOUNT_PALETTE = [
  "#0f766e", // teal
  "#00c2a3", // mint
  "#c2410c", // orange
  "#1d4ed8", // blue
  "#be185d", // pink
  "#15803d", // green
  "#a16207", // amber
  "#b91c1c", // red
  "#0369a1", // sky
  "#4f46e5", // indigo
  "#a21caf", // fuchsia
  "#3f6212", // lime
];

function hexToRgba(hex, alpha) {
  const h = String(hex || "").replace("#", "");
  if (h.length !== 6) return `rgba(100, 116, 139, ${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Índice de paleta: preferir posición entre cuentas conectadas
 * (así N cuentas → N colores distintos garantizados).
 */
export function accountPaletteIndex(email, connectedEmails = []) {
  const norm = normalizeAccountEmail(email);
  if (!norm.includes("@")) return -1;

  const list = normalizeConnectedEmails(connectedEmails);
  const idx = list.indexOf(norm);
  if (idx >= 0) return idx % ACCOUNT_PALETTE.length;

  // Cuenta no listada: hash del email completo (dominio + local).
  return hashEmail(norm) % ACCOUNT_PALETTE.length;
}

export function accountPaletteSolid(email, connectedEmails = []) {
  const idx = accountPaletteIndex(email, connectedEmails);
  if (idx < 0) return "#64748b";
  return ACCOUNT_PALETTE[idx];
}

/** @deprecated hue HSL; usar accountPaletteSolid */
export function accountHue(email, connectedEmails = []) {
  const solid = accountPaletteSolid(email, connectedEmails);
  // Aproximación solo por compatibilidad
  const idx = accountPaletteIndex(email, connectedEmails);
  if (idx < 0) return 187;
  return Math.round((idx * 360) / ACCOUNT_PALETTE.length) % 360;
}

/** Color estable y distinguible por dirección de cuenta. */
export function accountColor(email, connectedEmails = []) {
  if (!email || !String(email).includes("@")) return "#64748b";
  return accountPaletteSolid(email, connectedEmails);
}

/** Estilos de chip para la cuenta receptora en listas. */
export function accountChipSx(email, neutral = {}, connectedEmails = []) {
  if (!email || !String(email).includes("@")) {
    return {
      bgcolor: neutral.metaBg || "rgba(100, 116, 139, 0.1)",
      borderColor: neutral.border || "rgba(100, 116, 139, 0.28)",
      color: neutral.textMuted || "#64748b",
      fontWeight: 600,
    };
  }

  const color = accountColor(email, connectedEmails);
  return {
    bgcolor: hexToRgba(color, 0.14),
    borderColor: hexToRgba(color, 0.42),
    color,
    fontWeight: 700,
  };
}

/** Color suave para acentos en filas (barra lateral, menos saturación). */
export function accountColorSoft(email, connectedEmails = []) {
  if (!email || !String(email).includes("@")) return "rgba(100, 116, 139, 0.32)";
  return hexToRgba(accountPaletteSolid(email, connectedEmails), 0.55);
}

/** Badge de no leídos / punto de cuenta: color sólido por buzón. */
export function accountBadgeColor(email, connectedEmails = []) {
  if (!email || !String(email).includes("@")) return "#00bbcd";
  return accountPaletteSolid(email, connectedEmails);
}

/** Versión clara del badge (leídos, sin número). */
export function accountBadgeColorSoft(email, connectedEmails = []) {
  if (!email || !String(email).includes("@")) return "hsla(187, 70%, 42%, 0.28)";
  return hexToRgba(accountPaletteSolid(email, connectedEmails), 0.38);
}

/** Tinte de fila: borde de cuenta + barra lateral un poco más marcada. */
export function accountRowTint(email, connectedEmails = []) {
  if (!email || !String(email).includes("@")) return null;

  const solid = accountPaletteSolid(email, connectedEmails);
  return {
    accent: solid,
    border: hexToRgba(solid, 0.35),
    borderHover: hexToRgba(solid, 0.5),
    bg: hexToRgba(solid, 0.06),
    bgHover: hexToRgba(solid, 0.1),
    barWidth: 3,
  };
}

export function resolveRowTintEmail(accountList = [], lastAccountEmail = "") {
  if (accountList.length === 1) return accountList[0];
  if (accountList.length > 1) return null;
  const fromLast = String(lastAccountEmail || "").trim();
  if (fromLast.includes("@")) return fromLast;
  return null;
}
